import Checkbox from '@rio-cloud/rio-uikit/Checkbox';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { ServiceTypeName } from '#components/order/ServiceTypeName.tsx';
import { StopTimeWindow } from '#components/shared/StopInfo.tsx';
import { isServiceOrder, type OrderSummary } from '#domain/Order.ts';

import { TourListCard } from './TourListCard.tsx';

type OpenOrdersListProps = {
    orders: OrderSummary[];
    selectedOrderIds: string[];
    onSelectedOrderIdsChange: (selectedOrderIds: string[]) => void;
};

type OpenOrderCardProps = {
    order: OrderSummary;
    selected: boolean;
    onSelectedChange: (selected: boolean) => void;
};

const OpenOrderCard = ({ order, selected, onSelectedChange }: OpenOrderCardProps) => {
    const intl = useIntl();
    const serviceOrder = isServiceOrder(order);
    const primaryStop = serviceOrder ? order.executionStop : order.loadingStop;
    const destination = serviceOrder ? undefined : order.unloadingStop.location.city;
    const checkboxId = `tour-order-${order.id}`;
    const accessibleOrderName = serviceOrder
        ? intl.formatMessage({
              id: `intl-msg:ordercom-cloud-frontend.service.type.${order.number}`,
              defaultMessage: order.number,
          })
        : order.number;

    return (
        <div className='position-relative width-100pct'>
            <TourListCard
                panelStyle={selected ? 'primary' : 'default'}
                testId='open-order-card'
                leading={
                    <Checkbox
                        id={checkboxId}
                        checked={selected}
                        custom
                        aria-label={accessibleOrderName}
                        className='margin-0'
                        onChange={() => onSelectedChange(!selected)}
                    >
                        <span className='checkbox-text-wrapper'>
                            <span className='checkbox-text' />
                        </span>
                    </Checkbox>
                }
            >
                <div className='display-flex flex-column gap-2 max-width-400' data-testid='open-order-card-details'>
                    <div className='text-size-16 line-height-18 ellipsis-1'>
                        <span
                            className={`rioglyph ${serviceOrder ? 'rioglyph-wrench' : 'rioglyph-truck'} margin-right-5`}
                            title={intl.formatMessage({
                                id: serviceOrder
                                    ? 'intl-msg:ordercom-cloud-frontend.order.type.service'
                                    : 'intl-msg:ordercom-cloud-frontend.order.type.transport',
                            })}
                        />
                        <span className='text-bold'>
                            {serviceOrder ? <ServiceTypeName value={order.number} /> : order.number}
                        </span>
                        <span className='text-color-gray margin-left-5'>{order.customerName}</span>
                    </div>
                    <div className='text-medium ellipsis-1'>
                        {primaryStop.location.country} - {primaryStop.location.zipCode} {primaryStop.location.city}
                        {destination && ` → ${destination}`}
                    </div>
                    <StopTimeWindow from={primaryStop.startTarget} to={primaryStop.endTarget} />
                </div>
            </TourListCard>
            <label
                aria-hidden='true'
                className='position-absolute top-0 right-0 bottom-0 left-0 cursor-pointer'
                data-testid='open-order-card-click-target'
                htmlFor={checkboxId}
            />
        </div>
    );
};

export const OpenOrdersList = ({ orders, selectedOrderIds, onSelectedOrderIdsChange }: OpenOrdersListProps) => {
    const intl = useIntl();
    const selectedOrderIdSet = useMemo(() => new Set(selectedOrderIds), [selectedOrderIds]);
    const sortedOrders = useMemo(
        () =>
            [...orders].sort(
                (first, second) => Number(selectedOrderIdSet.has(second.id)) - Number(selectedOrderIdSet.has(first.id))
            ),
        [orders, selectedOrderIdSet]
    );
    const title = intl.formatMessage({
        id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.orders',
        defaultMessage: 'Orders',
    });

    return (
        <section className='height-100pct min-height-300 display-flex flex-column' aria-label={title}>
            <div className='display-flex justify-content-between align-items-center gap-10 margin-bottom-10'>
                <h4 className='margin-0'>{title}</h4>
                <span className='text-color-gray'>
                    {intl.formatMessage(
                        {
                            id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.selected-count',
                            defaultMessage: '{selectedAmount} selected',
                        },
                        { selectedAmount: selectedOrderIds.length }
                    )}
                </span>
            </div>
            {sortedOrders.length === 0 ? (
                <div className='padding-20 text-center text-color-gray border rounded'>
                    {intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.orders.empty-state.title',
                        defaultMessage: 'No orders yet',
                    })}
                </div>
            ) : (
                <div className='min-height-0 overflow-y-auto flex-1-1' data-testid='open-orders-scroll-list'>
                    <div className='display-grid grid-cols-1 gap-10 padding-right-10'>
                        {sortedOrders.map(order => (
                            <OpenOrderCard
                                key={order.id}
                                order={order}
                                selected={selectedOrderIdSet.has(order.id)}
                                onSelectedChange={selected =>
                                    onSelectedOrderIdsChange(
                                        selected
                                            ? [...selectedOrderIds, order.id]
                                            : selectedOrderIds.filter(orderId => orderId !== order.id)
                                    )
                                }
                            />
                        ))}
                    </div>
                </div>
            )}
        </section>
    );
};
